import React, { useEffect, useState, useMemo, lazy, Suspense, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useI18n } from '../i18n';
import axios from 'axios';
import { Line } from 'react-chartjs-2';
import { Chart, CategoryScale, LinearScale, PointElement, LineElement, BarElement, Tooltip, Legend, TimeScale } from 'chart.js';
import 'chartjs-adapter-date-fns';

// Lazy load ChatBox
const ChatBox = lazy(() => import('../components/ChatBox'));

Chart.register(CategoryScale, LinearScale, TimeScale, PointElement, LineElement, BarElement, Tooltip, Legend);

const API_BASE = import.meta.env.VITE_API_URL || 'https://market360-api.onrender.com';

// Utility formatters
const formatCurrency = (v, currency = 'USD') => {
  if (v == null || Number.isNaN(Number(v))) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 2 }).format(Number(v));
};

const formatPercent = (v, digits = 2) => {
  if (v == null || Number.isNaN(Number(v))) return '—';
  const pct = Number(v);
  return `${pct >= 0 ? '+' : ''}${pct.toFixed(digits)}%`;
};

const formatNumber = (v) => {
  if (v == null || Number.isNaN(Number(v))) return '—';
  const num = Number(v);
  if (Math.abs(num) >= 1e9) return `${(num / 1e9).toFixed(2)}B`;
  if (Math.abs(num) >= 1e6) return `${(num / 1e6).toFixed(2)}M`;
  if (Math.abs(num) >= 1e3) return `${(num / 1e3).toFixed(1)}K`;
  return num.toFixed(0);
};

// Technical analysis calculators
const computeSMA = (values, len) => {
  const result = [];
  for (let i = 0; i < values.length; i++) {
    if (i < len - 1) {
      result.push(null);
    } else {
      const sum = values.slice(i - len + 1, i + 1).reduce((a, b) => a + b, 0);
      result.push(sum / len);
    }
  }
  return result;
};

const computeRSI = (values, period = 14) => {
  const result = [];
  let gains = 0, losses = 0;
  for (let i = 1; i < values.length; i++) {
    const change = values[i] - values[i - 1];
    if (i <= period) {
      if (change > 0) gains += change;
      else losses += Math.abs(change);
      if (i === period) {
        const rs = gains / losses || 0;
        result.push(100 - (100 / (1 + rs)));
      } else {
        result.push(null);
      }
    } else {
      const gain = change > 0 ? change : 0;
      const loss = change < 0 ? Math.abs(change) : 0;
      gains = (gains * (period - 1) + gain) / period;
      losses = (losses * (period - 1) + loss) / period;
      const rs = gains / losses || 0;
      result.push(100 - (100 / (1 + rs)));
    }
  }
  return result;
};

const computeStdDev = (values, len) => {
  const result = [];
  for (let i = 0; i < values.length; i++) {
    if (i < len - 1) {
      result.push(0);
    } else {
      const subset = values.slice(i - len + 1, i + 1);
      const mean = subset.reduce((a, b) => a + b, 0) / len;
      const variance = subset.reduce((a, b) => a + Math.pow(b - mean, 2), 0) / len;
      result.push(Math.sqrt(variance));
    }
  }
  return result;
};

const computeATR = (highs, lows, closes, period = 14) => {
  const result = [];
  const tr = [];
  for (let i = 0; i < highs.length; i++) {
    let trueRange;
    if (i === 0) {
      trueRange = highs[i] - lows[i];
    } else {
      const hl = highs[i] - lows[i];
      const hc = Math.abs(highs[i] - closes[i - 1]);
      const lc = Math.abs(lows[i] - closes[i - 1]);
      trueRange = Math.max(hl, hc, lc);
    }
    tr.push(trueRange);
  }
  let atr = null;
  for (let i = 0; i < tr.length; i++) {
    if (i < period - 1) {
      result.push(null);
    } else if (i === period - 1) {
      atr = tr.slice(0, period).reduce((a, b) => a + b, 0) / period;
      result.push(atr);
    } else {
      atr = (atr * (period - 1) + tr[i]) / period;
      result.push(atr);
    }
  }
  return result;
};

// Skeleton Loaders for progressive loading
const SkeletonLoader = ({ height = '20px', width = '100%', style = {} }) => (
  <div style={{
    height,
    width,
    background: 'linear-gradient(90deg, var(--bg-secondary) 25%, var(--bg-tertiary) 50%, var(--bg-secondary) 75%)',
    backgroundSize: '200% 100%',
    animation: 'loading 1.5s infinite',
    borderRadius: '4px',
    ...style
  }} />
);

const InsightsSkeletonSection = () => (
  <div className="insights-section card">
    <h3 style={{ marginBottom: '12px' }}>💡 Key Insights</h3>
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {[1, 2, 3].map(i => (
        <div key={i} style={{ padding: '8px' }}>
          <SkeletonLoader height="16px" width="90%" />
        </div>
      ))}
    </div>
  </div>
);

const NewsSkeletonSection = () => (
  <div className="news-section card" style={{ gridColumn: '1 / -1' }}>
    <h3 style={{ marginBottom: '12px' }}>📰 Latest News</h3>
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {[1, 2, 3].map(i => (
        <div key={i} style={{ padding: '12px', background: 'var(--bg-tertiary)', borderRadius: '8px' }}>
          <SkeletonLoader height="14px" width="60%" style={{ marginBottom: '8px' }} />
          <SkeletonLoader height="12px" width="40%" />
        </div>
      ))}
    </div>
  </div>
);

export default function StockDetailNew() {
  const { symbol } = useParams();
  const { t, lang } = useI18n();
  const navigate = useNavigate();

  // State
  const [details, setDetails] = useState(null);
  const [history, setHistory] = useState([]);
  const [indicators, setIndicators] = useState(null);
  const [prediction, setPrediction] = useState(null);
  const [insights, setInsights] = useState([]);
  const [news, setNews] = useState([]);
  const [backtest, setBacktest] = useState(null);
  const [similarStocks, setSimilarStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview'); // overview, analysis, backtest, ml-model
  const [selectedRange, setSelectedRange] = useState('1w'); // 1d, 1w, 1m, 3m, 6m, 1y, all
  const [chartType, setChartType] = useState('line');
  const [overlays, setOverlays] = useState({ ma5: false, ma20: false, ma50: false, ma200: false, rsi: false, bb: false, atr: false });
  const [retryTimer, setRetryTimer] = useState(null);
  const chartRef = useRef(null);

  // Load critical data first (details) for quick UI render
  useEffect(() => {
    if (!symbol) return;
    let mounted = true;

    async function loadCriticalData(retryCount = 0) {
      setLoading(true);
      try {
        const detailsRes = await axios.get(`${API_BASE}/api/yahoo/details/${symbol}`);
        if (!mounted) return;
        
        if (detailsRes.status === 200) {
          console.log('Details data loaded:', detailsRes.data);
          
          // Check if response contains error (rate limit)
          if (detailsRes.data.error) {
            // If we have cached data despite error, show it
            if (detailsRes.data.cached || detailsRes.data.price) {
              setDetails(detailsRes.data);
              setLoading(false);
              
              // Auto-retry after 60 seconds if rate limited
              if (detailsRes.data.rate_limited || detailsRes.data.error.includes('Rate limited')) {
                console.log('Rate limited with cached data, will auto-retry in 60 seconds...');
                const timer = setTimeout(() => {
                  if (mounted) {
                    console.log('Auto-retrying after rate limit...');
                    loadCriticalData(0);
                  }
                }, 60000); // 60 seconds
                setRetryTimer(timer);
              }
            } else {
              // No cached data available, retry faster
              if (retryCount < 1 && detailsRes.data.error.includes('Rate limited')) {
                console.log('Rate limited with no cache, retrying in 3 seconds...');
                setTimeout(() => {
                  if (mounted) loadCriticalData(retryCount + 1);
                }, 3000);
                return;
              }
              // Show error if no data and retries exhausted
              setDetails(detailsRes.data);
              setLoading(false);
            }
          } else {
            setDetails(detailsRes.data);
            setLoading(false); // Show UI immediately with details
          }
        }
      } catch (error) {
        console.error('Error loading details:', error);
        if (mounted) setLoading(false);
      }
    }

    loadCriticalData();
    return () => { 
      mounted = false;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [symbol]);

  // Load secondary data in background (analysis, insights, news, similar, prediction)
  useEffect(() => {
    if (!symbol) return;
    let mounted = true;

    async function loadSecondaryData() {
      try {
        const isVNStock = symbol.includes('.VN');
        const market = isVNStock ? 'VN' : 'US';
        
        console.log('Loading secondary data for:', { symbol, market, lang });
        
        // Load these in parallel but don't block UI
        const [analysisRes, predictionRes, insightsRes, newsRes, similarRes] = await Promise.allSettled([
          axios.get(`${API_BASE}/api/analysis/${symbol}`),
          axios.get(`${API_BASE}/api/predict/${symbol}?days_ahead=7`),
          axios.get(`${API_BASE}/api/insights/${symbol}${lang === 'vi' ? '?translate=vi' : ''}`),
          axios.get(`${API_BASE}/api/news/${symbol}?limit=10${lang === 'vi' ? '&translate=vi' : ''}`),
          axios.get(`${API_BASE}/api/similar/${symbol}?limit=6&market=${market}`)
        ]);

        if (!mounted) return;

        if (analysisRes.status === 'fulfilled') {
          console.log('Indicators data:', analysisRes.value.data);
          setIndicators(analysisRes.value.data);
        }
        if (predictionRes.status === 'fulfilled') {
          console.log('Prediction data:', predictionRes.value.data);
          setPrediction(predictionRes.value.data);
        }
        if (insightsRes.status === 'fulfilled') {
          const insights = insightsRes.value.data?.insights || [];
          console.log('Insights loaded:', { count: insights.length });
          setInsights(insights);
        }
        if (newsRes.status === 'fulfilled') {
          const newsData = newsRes.value.data?.articles || newsRes.value.data?.news || [];
          console.log('News loaded:', { count: newsData.length });
          if (newsData.length > 0) {
            setNews(newsData);
          } else {
            // Fallback: try stripped symbol (e.g., remove exchange suffix)
            const baseSymbol = symbol.split('.')[0] || symbol;
            try {
              const fallback = await axios.get(`${API_BASE}/api/news/${baseSymbol}?limit=10${lang === 'vi' ? '&translate=vi' : ''}`);
              const fbNews = fallback.data?.articles || fallback.data?.news || [];
              console.log('News fallback loaded:', { count: fbNews.length, baseSymbol });
              setNews(fbNews);
            } catch (fbErr) {
              console.warn('Fallback news fetch failed:', fbErr);
              setNews([]);
            }
          }
        }
        if (similarRes.status === 'fulfilled') {
          const similarData = similarRes.value.data?.similar_stocks || [];
          console.log('Similar stocks loaded:', { count: similarData.length });
          setSimilarStocks(similarData);
        }
      } catch (error) {
        console.error('Error loading secondary data:', error);
      }
    }

    // Delay secondary load slightly to prioritize initial render
    const timer = setTimeout(() => loadSecondaryData(), 300);
    return () => { clearTimeout(timer); mounted = false; };
  }, [symbol, lang]);

  // Load history data when time period changes (without reloading entire page)
  useEffect(() => {
    if (!symbol) return;
    let mounted = true;

    async function loadHistory() {
      try {
        const periodMap = {
          '1d': '5d',
          '1w': '1mo',
          '1m': '3mo',
          '3m': '3mo',
          '6m': '6mo',
          '1y': '1y',
          'all': 'max'
        };
        const intervalMap = {
          '1d': '1d',
          '1w': '1d',
          '1m': '1d',
          '3m': '1d',
          '6m': '1d',
          '1y': '1d',
          'all': '1wk'
        };
        const period = periodMap[selectedRange] || '3mo';
        const interval = intervalMap[selectedRange] || '1d';
        
        console.log('Loading history for period:', { selectedRange, period });
        const historyRes = await axios.get(`${API_BASE}/api/yahoo/history/${symbol}?period=${period}&interval=${interval}`);
        
        if (!mounted) return;
        
        const prices = historyRes.data?.history || historyRes.data?.prices || [];
        const transformedPrices = prices.map(p => ({
          ...p,
          date: p.timestamp || p.date
        }));
        console.log('History updated for period:', { period, count: transformedPrices.length });
        setHistory(transformedPrices);
      } catch (error) {
        console.error('Error loading history:', error);
      }
    }

    loadHistory();
    return () => { mounted = false; };
  }, [symbol, selectedRange]);

  // Load backtest when tab is active
  useEffect(() => {
    if (activeTab === 'backtest' && !backtest && symbol) {
      console.log('Loading backtest for:', symbol);
      setBacktest({ loading: true });
      
      axios.get(`${API_BASE}/api/daytrade/${symbol}?initial_balance=10000&risk_per_trade=0.02&period=30d&interval=5m`, {
        timeout: 60000 // 60 second timeout for backtest computation
      })
        .then(res => {
          console.log('Backtest loaded:', res.data);
          if (res.data.error) {
            setBacktest({ error: res.data.error });
          } else {
            setBacktest(res.data);
          }
        })
        .catch(err => {
          console.error('Backtest failed:', err);
          const errorMsg = err.response?.data?.error || err.message || 'Failed to load backtest data';
          setBacktest({ error: errorMsg });
        });
    }
  }, [activeTab, symbol, backtest]);

  // Chart data
  const chartData = useMemo(() => {
    if (!history.length) return null;
    return {
      labels: history.map(p => p.date),
      datasets: [{
        label: 'Close Price',
        data: history.map(p => p.close),
        borderColor: details?.change_percent >= 0 ? '#10b981' : '#ef4444',
        backgroundColor: details?.change_percent >= 0 ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
        fill: true,
        tension: 0.4
      }]
    };
  }, [history, details]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        callbacks: {
          label: (context) => `Price: ${formatCurrency(context.parsed.y)}`
        }
      }
    },
    scales: {
      x: {
        type: 'time',
        time: { unit: 'day' },
        grid: { color: 'rgba(255, 255, 255, 0.05)' },
        ticks: { color: '#b0b9c6' }
      },
      y: {
        grid: { color: 'rgba(255, 255, 255, 0.05)' },
        ticks: {
          color: '#b0b9c6',
          callback: (value) => formatCurrency(value)
        }
      }
    }
  };

  if (loading) {
    return (
      <div className="stock-detail-container">
        <div className="loading-screen">
          <div className="spinner"></div>
          <p>Loading {symbol}...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="stock-detail-container">
      {/* Rate Limit Error Banner */}
      {details?.error && (
        <div style={{
          padding: '12px 20px',
          background: details.cached ? 'rgba(251, 191, 36, 0.1)' : 'rgba(239, 68, 68, 0.1)',
          border: details.cached ? '1px solid rgba(251, 191, 36, 0.3)' : '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '8px',
          margin: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <span style={{ fontSize: '20px' }}>{details.cached ? '⏱️' : '⚠️'}</span>
          <div style={{ flex: 1 }}>
            <h4 style={{ margin: '0 0 4px 0', color: details.cached ? '#f59e0b' : '#ef4444', fontSize: '14px', fontWeight: '600' }}>
              {details.cached ? 'Showing Cached Data' : 'Data Temporarily Unavailable'}
            </h4>
            <p style={{ margin: '0 0 8px 0', fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.4' }}>
              {details.cached ? (
                <>
                  Yahoo Finance is rate limiting. Showing data from <strong>{details.cache_age}</strong> ago.
                  {details.rate_limited && ' Auto-retrying in background every 60 seconds...'}
                </>
              ) : (
                <>
                  {details.error}. Our data provider is rate limiting requests. Try refreshing in a moment.
                </>
              )}
            </p>
            {!details.cached && (
              <button
                onClick={() => window.location.reload()}
                style={{
                  padding: '6px 12px',
                  fontSize: '12px',
                  fontWeight: '500',
                  color: '#fff',
                  background: '#3b82f6',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => e.target.style.background = '#2563eb'}
                onMouseOut={(e) => e.target.style.background = '#3b82f6'}
              >
                🔄 Retry Now
              </button>
            )}
          </div>
        </div>
      )}
      
      {/* Header with Buy Button */}
      <div className="detail-header">
        <div className="header-content">
          <div className="header-left">
            <button onClick={() => navigate(-1)} className="back-button">
              ← Back
            </button>
            <div className="symbol-info">
              <h1>{symbol}</h1>
              <p className="company-name">{details?.name || details?.longName}</p>
              {!details?.error && (
                <div className="price-badge">
                  <span className="price">{formatCurrency(details?.currentPrice || details?.price)}</span>
                  <span className={`change ${details?.change_percent >= 0 ? 'positive' : 'negative'}`}>
                    {formatPercent(details?.change_percent)}
                  </span>
                </div>
              )}
            </div>
          </div>
          {/* <button className="buy-button">
            🛒 Buy Now
          </button> */}
        </div>
      </div>

      {/* Tabs */}
      <div className="detail-tabs">
        <button 
          className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          📊 Overview
        </button>
        <button 
          className={`tab ${activeTab === 'analysis' ? 'active' : ''}`}
          onClick={() => setActiveTab('analysis')}
        >
          📈 Technical Analysis
        </button>
        <button 
          className={`tab ${activeTab === 'backtest' ? 'active' : ''}`}
          onClick={() => setActiveTab('backtest')}
        >
          🔬 Backtest
        </button>
        <button 
          className={`tab ${activeTab === 'ml-model' ? 'active' : ''}`}
          onClick={() => setActiveTab('ml-model')}
        >
          🤖 AI Prediction
        </button>
      </div>

      {/* Content */}
      <div className="detail-content">
        {activeTab === 'overview' && (
          <OverviewTab 
            details={details}
            history={history}
            chartData={chartData}
            chartOptions={chartOptions}
            insights={insights}
            news={news}
            indicators={indicators}
            similarStocks={similarStocks}
            selectedRange={selectedRange}
            setSelectedRange={setSelectedRange}
          />
        )}
        {activeTab === 'analysis' && (
          <AnalysisTab 
            indicators={indicators}
            history={history}
          />
        )}
        {activeTab === 'backtest' && (
          <BacktestTab 
            backtest={backtest}
            symbol={symbol}
          />
        )}
        {activeTab === 'ml-model' && (
          <MLModelTab 
            prediction={prediction}
            symbol={symbol}
            indicators={indicators}
            history={history}
            details={details}
            insights={insights}
            similarStocks={similarStocks}
          />
        )}
      </div>

      {/* ChatBox */}
      <Suspense fallback={<div style={{color:'var(--text-secondary)', padding: '1rem'}}>Loading chat…</div>}>
        <ChatBox apiBase={API_BASE} symbol={symbol} initialOpen={false} />
      </Suspense>
    </div>
  );
}

// Overview Tab Component
function OverviewTab({ details, history, chartData, chartOptions, insights, news, indicators, similarStocks, selectedRange, setSelectedRange }) {
  const [overlays, setOverlays] = useState({ ma5: false, ma20: false, ma50: false, ma200: false, rsi: false, bb: false, atr: false });
  const [chartType, setChartType] = useState('line');
  const [historicalDataCount, setHistoricalDataCount] = useState(30); // Default to 30 rows
  const chartRef = useRef(null);

  // Calculate real AI results based on technical indicators
  const aiResults = useMemo(() => {
    console.log('Calculating AI results:', { indicators, historyLength: history?.length });
    if (!indicators || !history || history.length === 0) {
      console.log('Missing data for AI calculation:', { hasIndicators: !!indicators, historyLength: history?.length });
      return {
        patternScore: 0,
        patterns: []
      };
    }

    // Calculate pattern score based on technical indicators
    let patternScore = 50; // Base score
    
    // RSI contribution (30-70 range is ideal)
    if (indicators.rsi) {
      const rsiScore = indicators.rsi > 30 && indicators.rsi < 70 ? 20 : 
                       indicators.rsi > 70 ? 10 : 5;
      patternScore += rsiScore;
    }
    
    // MACD contribution (positive signal is bullish)
    if (indicators.macd && indicators.macd_signal) {
      const macdBullish = indicators.macd > indicators.macd_signal;
      patternScore += macdBullish ? 15 : -10;
    }
    
    // Moving average trend (price above MA50 is bullish)
    if (indicators.sma50 && details?.currentPrice) {
      const aboveMA = details.currentPrice > indicators.sma50;
      patternScore += aboveMA ? 10 : -5;
    }

    // Volume trend analysis
    const recentVolumes = history.slice(-10).map(h => h.volume || 0);
    const avgVolume = recentVolumes.reduce((a, b) => a + b, 0) / recentVolumes.length;
    const lastVolume = recentVolumes[recentVolumes.length - 1] || 0;
    const volumeIncrease = lastVolume > avgVolume;
    
    // Price momentum (last 5 days)
    const recentPrices = history.slice(-5).map(h => h.close || 0);
    const momentum = recentPrices.length > 1 ? 
      ((recentPrices[recentPrices.length - 1] - recentPrices[0]) / recentPrices[0]) * 100 : 0;

    // Constrain score between 0-100
    patternScore = Math.max(0, Math.min(100, Math.round(patternScore)));
    console.log('Pattern score calculated:', { patternScore, rsi: indicators.rsi, macd: indicators.macd });

    // Calculate pattern percentages based on indicators
    const patterns = [
      { 
        name: 'Technical Momentum', 
        percentage: Math.round(Math.abs(momentum) * 10), 
        timeframe: '5 Days',
        increase: momentum > 0
      },
      { 
        name: 'Volume Trend', 
        percentage: volumeIncrease ? Math.round((lastVolume / avgVolume - 1) * 100) : 0,
        timeframe: '10 Days Average',
        increase: volumeIncrease
      },
      { 
        name: 'RSI Signal', 
        percentage: indicators.rsi ? Math.round(Math.abs(50 - indicators.rsi)) : 0,
        timeframe: '14 Days',
        increase: indicators.rsi && indicators.rsi < 50
      }
    ].filter(p => p.percentage > 0 && p.percentage <= 100);

    return {
      patternScore,
      patterns
    };
  }, [indicators, history, details]);

  const patternMatches = useMemo(() => {
    if (!history || history.length < 60) return [];

    const sortedHistory = [...history].sort((a, b) => new Date(a.date) - new Date(b.date));
    const closes = sortedHistory.map(h => h.close || 0);
    const volumes = sortedHistory.map(h => h.volume || 0);
    const dates = sortedHistory.map(h => new Date(h.date));

    const ma50 = computeSMA(closes, 50);
    const vol20 = computeSMA(volumes, 20);
    const rsi14 = computeRSI(closes, 14);

    const matches = [];
    for (let i = 60; i < sortedHistory.length - 6; i++) {
      const aboveMa = ma50[i] && closes[i] > ma50[i];
      const volSpike = vol20[i] && volumes[i] > vol20[i] * 1.2;
      const rsiBull = rsi14[i] && rsi14[i] > 50;
      if (aboveMa && volSpike && rsiBull) {
        const forward5 = closes[i + 5] ? ((closes[i + 5] - closes[i]) / closes[i]) * 100 : null;
        matches.push({
          date: dates[i].toISOString().slice(0, 10),
          price: closes[i],
          volume: volumes[i],
          volAvg: vol20[i],
          rsi: rsi14[i],
          forward5
        });
      }
    }

    return matches.slice(-3).reverse(); // most recent three matches
  }, [history]);

  // Build advanced chart with overlays
  const advancedChartData = useMemo(() => {
    console.log('OverviewTab - Building chart data:', { historyLength: history?.length, overlays });
    if (!history || !history.length) {
      console.log('No history data available for chart');
      return null;
    }

    // Sort history chronologically (oldest first) for accurate technical indicator calculations
    const sortedHistory = [...history].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    
    const closes = sortedHistory.map(h => h.close || 0);
    const highs = sortedHistory.map(h => h.high || 0);
    const lows = sortedHistory.map(h => h.low || 0);
    const dates = sortedHistory.map(h => new Date(h.date).getTime());
    
    const datasets = [];

    // Main price line
    datasets.push({
      label: 'Close Price',
      data: history.map((p, i) => ({ x: dates[i], y: p.close })),
      borderColor: details?.change_percent >= 0 ? '#10b981' : '#ef4444',
      backgroundColor: details?.change_percent >= 0 ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
      borderWidth: 2,
      fill: true,
      tension: 0.4,
      parsing: false,
      yAxisID: 'y'
    });

    // Moving averages
    const ma5 = computeSMA(closes, 5);
    const ma20 = computeSMA(closes, 20);
    const ma50 = computeSMA(closes, 50);
    const ma200 = computeSMA(closes, 200);

    if (overlays.ma5) {
      datasets.push({
        label: 'MA5',
        data: ma5.map((v, i) => v ? { x: dates[i], y: v } : null).filter(d => d),
        borderColor: '#f97316',
        borderWidth: 1,
        pointRadius: 0,
        parsing: false,
        yAxisID: 'y'
      });
    }

    if (overlays.ma20) {
      datasets.push({
        label: 'MA20',
        data: ma20.map((v, i) => v ? { x: dates[i], y: v } : null).filter(d => d),
        borderColor: '#60a5fa',
        borderWidth: 1,
        pointRadius: 0,
        parsing: false,
        yAxisID: 'y'
      });
    }

    if (overlays.ma50) {
      datasets.push({
        label: 'MA50',
        data: ma50.map((v, i) => v ? { x: dates[i], y: v } : null).filter(d => d),
        borderColor: '#a78bfa',
        borderWidth: 1,
        pointRadius: 0,
        parsing: false,
        yAxisID: 'y'
      });
    }

    if (overlays.ma200) {
      datasets.push({
        label: 'MA200',
        data: ma200.map((v, i) => v ? { x: dates[i], y: v } : null).filter(d => d),
        borderColor: '#94a3b8',
        borderWidth: 1,
        pointRadius: 0,
        parsing: false,
        yAxisID: 'y'
      });
    }

    // Bollinger Bands
    if (overlays.bb) {
      const bbStd = computeStdDev(closes, 20);
      const bbMa = computeSMA(closes, 20);
      datasets.push({
        label: 'BB Upper',
        data: bbMa.map((v, i) => v && bbStd[i] ? { x: dates[i], y: v + 2 * bbStd[i] } : null).filter(d => d),
        borderColor: '#34d399',
        borderWidth: 0.5,
        pointRadius: 0,
        borderDash: [5, 5],
        parsing: false,
        yAxisID: 'y'
      });
      datasets.push({
        label: 'BB Lower',
        data: bbMa.map((v, i) => v && bbStd[i] ? { x: dates[i], y: v - 2 * bbStd[i] } : null).filter(d => d),
        borderColor: '#34d399',
        borderWidth: 0.5,
        pointRadius: 0,
        borderDash: [5, 5],
        parsing: false,
        yAxisID: 'y'
      });
    }

    // RSI
    if (overlays.rsi) {
      const rsiArr = computeRSI(closes, 14);
      datasets.push({
        label: 'RSI',
        data: rsiArr.map((v, i) => v ? { x: dates[i], y: v } : null).filter(d => d),
        borderColor: '#fb7185',
        borderWidth: 1,
        pointRadius: 0,
        parsing: false,
        yAxisID: 'y2'
      });
    }

    // ATR
    if (overlays.atr) {
      const atrArr = computeATR(highs, lows, closes, 14);
      datasets.push({
        label: 'ATR',
        data: atrArr.map((v, i) => v ? { x: dates[i], y: v } : null).filter(d => d),
        borderColor: '#f472b6',
        borderWidth: 1,
        pointRadius: 0,
        parsing: false,
        yAxisID: 'y'
      });
    }

    console.log('Chart datasets built:', { datasetCount: datasets.length, firstDataset: datasets[0]?.data?.length });
    return { datasets };
  }, [history, overlays, details]);

  const advancedChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false
    },
    plugins: {
      legend: {
        display: true,
        position: 'top'
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        callbacks: {
          label: (context) => {
            const label = context.dataset.label || '';
            const value = context.parsed.y;
            return `${label}: ${typeof value === 'number' ? value.toFixed(2) : value}`;
          }
        }
      }
    },
    scales: {
      x: {
        type: 'time',
        time: { unit: 'day' },
        grid: { color: 'rgba(255, 255, 255, 0.05)' },
        ticks: { color: '#b0b9c6' }
      },
      y: {
        grid: { color: 'rgba(255, 255, 255, 0.05)' },
        ticks: {
          color: '#b0b9c6',
          callback: (value) => formatCurrency(value)
        }
      },
      y2: {
        type: 'linear',
        position: 'right',
        min: 0,
        max: 100,
        grid: { drawOnChartArea: false },
        ticks: {
          color: '#b0b9c6'
        }
      }
    }
  };

  return (
    <div className="overview-tab">
      {/* Historical Pattern Matching - show real similar stocks */}
      {/* NOTE: This section has been moved to AI Prediction tab */}

      {/* Overview Grid */}
      <div className="overview-grid">
        {/* Chart Section with Advanced Features */}
        <div className="chart-section card" style={{ gridColumn: '1 / -1' }}>
          <div style={{ marginBottom: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h3>Advanced Price Chart ({selectedRange.toUpperCase() === '1D' ? '1 Day' : selectedRange.toUpperCase() === '1W' ? '1 Week' : selectedRange.toUpperCase() === '1M' ? '1 Month' : selectedRange.toUpperCase() === '3M' ? '3 Months' : selectedRange.toUpperCase() === '6M' ? '6 Months' : selectedRange.toUpperCase() === '1Y' ? '1 Year' : 'All Time'})</h3>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <select
                  value={chartType}
                  onChange={(e) => setChartType(e.target.value)}
                  style={{
                    background: 'var(--bg-glass)',
                    border: '1px solid var(--border-color)',
                    padding: '6px 8px',
                    borderRadius: '8px',
                    color: 'var(--text-primary)'
                  }}
                >
                  <option value="line">Line</option>
                  <option value="bar">Bar</option>
                </select>
              </div>
            </div>

            {/* Chart Period Controls */}
            <div style={{
              display: 'flex',
              gap: '8px',
              padding: '12px 0',
              borderBottom: '1px solid var(--border-color)',
              marginBottom: '12px',
              overflowX: 'auto'
            }}>
              <span style={{ fontSize: '12px', color: 'var(--text-secondary)', paddingRight: '12px', whiteSpace: 'nowrap' }}>Period:</span>
              {['1d', '1w', '1m', '3m', '6m', '1y', 'all'].map(range => (
                <button
                  key={range}
                  onClick={() => setSelectedRange(range)}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '4px',
                    border: selectedRange === range ? 'none' : '1px solid var(--border-color)',
                    background: selectedRange === range ? '#60a5fa' : 'transparent',
                    color: selectedRange === range ? '#ffffff' : 'var(--text-primary)',
                    cursor: 'pointer',
                    fontSize: '12px',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {range.toUpperCase()}
                </button>
              ))}
            </div>

            {/* Overlay Controls */}
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Overlays:</span>
              {[
                { key: 'ma5', label: 'MA5', color: '#f97316' },
                { key: 'ma20', label: 'MA20', color: '#60a5fa' },
                { key: 'ma50', label: 'MA50', color: '#a78bfa' },
                { key: 'ma200', label: 'MA200', color: '#94a3b8' },
                { key: 'rsi', label: 'RSI', color: '#fb7185' },
                { key: 'bb', label: 'BB', color: '#34d399' },
                { key: 'atr', label: 'ATR', color: '#f472b6' }
              ].map(o => (
                <button
                  key={o.key}
                  onClick={() => setOverlays(s => ({ ...s, [o.key]: !s[o.key] }))}
                  style={{
                    padding: '6px 8px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    background: overlays[o.key] ? o.color : 'var(--bg-secondary)',
                    color: overlays[o.key] ? '#ffffff' : 'var(--text-primary)',
                    border: overlays[o.key] ? 'none' : '1px solid var(--border-color)',
                    cursor: 'pointer'
                  }}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>

          <div style={{ height: '400px' }}>
            {advancedChartData && advancedChartData.datasets && advancedChartData.datasets.length > 0 ? (
              <Line ref={chartRef} data={advancedChartData} options={advancedChartOptions} />
            ) : (
              <p style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>
                {history && history.length > 0 ? 'Building chart...' : 'Loading chart data...'}
              </p>
            )}
          </div>
        </div>

        {/* Key Metrics */}
        <div className="metrics-section card">
          <h3>Key Metrics</h3>
          <div className="metrics-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px' }}>
            <div className="metric">
              <span className="metric-label">Open</span>
              <span className="metric-value">{formatCurrency(details?.open)}</span>
            </div>
            <div className="metric">
              <span className="metric-label">Day High</span>
              <span className="metric-value">{formatCurrency(details?.dayHigh)}</span>
            </div>
            <div className="metric">
              <span className="metric-label">Day Low</span>
              <span className="metric-value">{formatCurrency(details?.dayLow)}</span>
            </div>
            <div className="metric">
              <span className="metric-label">Volume</span>
              <span className="metric-value">{formatNumber(details?.volume)}</span>
            </div>
            <div className="metric">
              <span className="metric-label">52W High</span>
              <span className="metric-value">{formatCurrency(details?.high52)}</span>
            </div>
            <div className="metric">
              <span className="metric-label">52W Low</span>
              <span className="metric-value">{formatCurrency(details?.low52)}</span>
            </div>
            <div className="metric">
              <span className="metric-label">Market Cap</span>
              <span className="metric-value">{formatNumber(details?.marketCap)}</span>
            </div>
            <div className="metric">
              <span className="metric-label">P/E Ratio</span>
              <span className="metric-value">{details?.peRatio ? details.peRatio.toFixed(2) : '—'}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Historical Price Table */}
      <div className="historical-section card" style={{ gridColumn: '1 / -1' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <h3>📊 Historical Prices</h3>
          <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
            Showing {Math.min(historicalDataCount, history.length)} of {history.length} records
          </div>
        </div>
        <div className="historical-table-container" style={{ overflowX: 'auto', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
          {history.length > 0 ? (
            <>
              <table className="historical-table" style={{
                width: '100%',
                borderCollapse: 'collapse',
                minWidth: '700px'
              }}>
                <thead>
                  <tr style={{ background: 'var(--bg-tertiary)', borderBottom: '1px solid var(--border-color)' }}>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '700', color: 'var(--text-secondary)' }}>Date</th>
                    <th style={{ padding: '12px', textAlign: 'right', fontWeight: '700', color: 'var(--text-secondary)' }}>Open</th>
                    <th style={{ padding: '12px', textAlign: 'right', fontWeight: '700', color: 'var(--text-secondary)' }}>High</th>
                    <th style={{ padding: '12px', textAlign: 'right', fontWeight: '700', color: 'var(--text-secondary)' }}>Low</th>
                    <th style={{ padding: '12px', textAlign: 'right', fontWeight: '700', color: 'var(--text-secondary)' }}>Close</th>
                    <th style={{ padding: '12px', textAlign: 'right', fontWeight: '700', color: 'var(--text-secondary)' }}>Volume</th>
                    <th style={{ padding: '12px', textAlign: 'center', fontWeight: '700', color: 'var(--text-secondary)' }}>Change %</th>
                  </tr>
                </thead>
                <tbody>
                  {history.slice().reverse().slice(0, historicalDataCount).map((row, idx) => {
                    const dateLabel = new Date(row.date).toLocaleDateString();
                    const changeColor = (row.close || 0) >= (row.open || 0) ? '#10b981' : '#ef4444';
                    const changePct = row.open ? ((row.close - row.open) / row.open * 100) : 0;
                    return (
                      <tr
                        key={idx}
                        style={{
                          borderBottom: '1px solid var(--border-color)',
                          background: idx % 2 === 0 ? 'transparent' : 'rgba(255, 255, 255, 0.01)',
                          transition: 'background 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = idx % 2 === 0 ? 'transparent' : 'rgba(255, 255, 255, 0.01)'}
                      >
                        <td style={{ padding: '12px', fontSize: '13px' }}>{dateLabel}</td>
                        <td style={{ padding: '12px', textAlign: 'right', fontSize: '13px', color: changeColor }}>{formatCurrency(row.open)}</td>
                        <td style={{ padding: '12px', textAlign: 'right', fontSize: '13px', color: '#60a5fa' }}>{formatCurrency(row.high)}</td>
                        <td style={{ padding: '12px', textAlign: 'right', fontSize: '13px', color: '#60a5fa' }}>{formatCurrency(row.low)}</td>
                        <td style={{ padding: '12px', textAlign: 'right', fontSize: '13px', color: changeColor, fontWeight: '700' }}>{formatCurrency(row.close)}</td>
                        <td style={{ padding: '12px', textAlign: 'right', fontSize: '13px', color: 'var(--text-secondary)' }}>{formatNumber(row.volume)}</td>
                        <td style={{ padding: '12px', textAlign: 'center', fontSize: '13px', color: changePct >= 0 ? '#10b981' : '#ef4444', fontWeight: '700' }}>
                          {changePct >= 0 ? '+' : ''}{changePct.toFixed(2)}%
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {historicalDataCount < history.length && (
                <div style={{ padding: '12px', textAlign: 'center', borderTop: '1px solid var(--border-color)' }}>
                  <button
                    onClick={() => setHistoricalDataCount(prev => Math.min(prev + 30, history.length))}
                    style={{
                      padding: '8px 20px',
                      background: '#60a5fa',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: '600'
                    }}
                  >
                    Load More ({history.length - historicalDataCount} remaining)
                  </button>
                </div>
              )}
            </>
          ) : (
            <p className="empty" style={{ padding: '20px', color: 'var(--text-secondary)' }}>No historical data available</p>
          )}
        </div>
      </div>

      {/* Latest News */}
      <div className="news-section card" style={{ gridColumn: '1 / -1' }}>
        <h3>📰 Latest News</h3>
        <div className="news-list">
          {news.length > 0 ? (
            news.slice(0, 5).map((article, idx) => (
              <a
                key={idx}
                href={article.link || article.url}
                target="_blank"
                rel="noopener noreferrer"
                className="news-item"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.75rem',
                  padding: '1rem',
                  background: 'var(--bg-tertiary)',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  color: 'var(--text-primary)',
                  transition: 'background 0.2s',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-secondary)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'var(--bg-tertiary)'}
              >
                <h4 style={{ margin: '0', fontSize: '15px', fontWeight: '700', color: '#60a5fa', lineHeight: '1.4', wordWrap: 'break-word' }}>
                  {article.title_translated || article.title || article.headline}
                </h4>
                {article.summary && (
                  <p style={{ margin: '0', fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.5', wordWrap: 'break-word' }}>
                    {article.summary_translated || article.summary}
                  </p>
                )}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: '12px',
                    color: 'var(--text-secondary)',
                    marginTop: '0.5rem'
                  }}
                >
                  <span>{article.publisher || article.source || 'Unknown'}</span>
                  {article.published && (
                    <span>{new Date(article.published).toLocaleDateString()}</span>
                  )}
                </div>
              </a>
            ))
          ) : (
            <p className="empty" style={{ color: 'var(--text-secondary)' }}>No news available</p>
          )}
        </div>
      </div>
    </div>
    );
}

// Technical Analysis Tab
function AnalysisTab({ indicators, history }) {
  if (!indicators) {
    return <div className="card"><p style={{ color: 'var(--text-secondary)' }}>No technical analysis data available</p></div>;
  }

  const technicalIndicators = [
    {
      name: 'RSI (Relative Strength Index)',
      value: indicators.rsi ? `${indicators.rsi.toFixed(2)}` : 'N/A',
      formula: 'RSI = 100 - (100 / (1 + RS)), where RS = Avg Gain / Avg Loss over 14 periods',
      explanation: 'RSI measures momentum on a scale of 0-100. Values above 70 indicate overbought conditions (potential sell signal), while values below 30 indicate oversold conditions (potential buy signal).',
      signal: indicators.rsi ? (indicators.rsi > 70 ? 'Overbought ⚠️' : indicators.rsi < 30 ? 'Oversold ✅' : 'Neutral') : '—'
    },
    {
      name: 'MACD (Moving Average Convergence Divergence)',
      value: indicators.macd && indicators.macd_signal ? `${indicators.macd.toFixed(4)} / ${indicators.macd_signal.toFixed(4)}` : 'N/A',
      formula: 'MACD = EMA(12) - EMA(26), Signal = EMA(9) of MACD',
      explanation: 'MACD shows the relationship between two moving averages. When MACD crosses above the signal line, it\'s a bullish signal. When it crosses below, it\'s bearish.',
      signal: indicators.macd && indicators.macd_signal ? (indicators.macd > indicators.macd_signal ? 'Bullish 📈' : 'Bearish 📉') : '—'
    },
    {
      name: 'Bollinger Bands',
      value: indicators.bb_lower && indicators.bb_upper ? `${indicators.bb_lower.toFixed(2)} - ${indicators.bb_upper.toFixed(2)}` : 'N/A',
      formula: 'Upper Band = SMA(20) + 2σ, Lower Band = SMA(20) - 2σ',
      explanation: 'Bollinger Bands measure volatility. Price touching the upper band suggests overbought conditions, while touching the lower band suggests oversold. Wide bands indicate high volatility.',
      signal: 'Volatility bands active'
    },
    {
      name: 'EMA (Exponential Moving Average)',
      value: indicators.ema_12 && indicators.ema_26 ? `12: ${indicators.ema_12.toFixed(2)}, 26: ${indicators.ema_26.toFixed(2)}` : 'N/A',
      formula: 'EMA = Price(t) × k + EMA(y) × (1 − k), where k = 2/(N+1)',
      explanation: 'EMAs give more weight to recent prices. When short-term EMA (12) crosses above long-term EMA (26), it signals uptrend (golden cross). Opposite is death cross.',
      signal: indicators.ema_12 && indicators.ema_26 ? (indicators.ema_12 > indicators.ema_26 ? 'Golden Cross 🌟' : 'Death Cross ⚰️') : '—'
    },
    {
      name: 'ADX (Average Directional Index)',
      value: indicators.adx ? `${indicators.adx.toFixed(2)}` : 'N/A',
      formula: 'ADX = 100 × EMA of DX over 14 periods',
      explanation: 'ADX measures trend strength from 0-100. Values above 25 indicate strong trend, below 20 indicates weak/no trend. It doesn\'t show direction, only strength.',
      signal: indicators.adx ? (indicators.adx > 25 ? 'Strong Trend 💪' : 'Weak Trend') : '—'
    },
    {
      name: 'Stochastic Oscillator',
      value: indicators.stoch_k && indicators.stoch_d ? `${indicators.stoch_k.toFixed(2)} / ${indicators.stoch_d.toFixed(2)}` : 'N/A',
      formula: '%K = 100 × (Close - Low14) / (High14 - Low14), %D = SMA(%K, 3)',
      explanation: 'Stochastic compares closing price to price range over time. Values above 80 suggest overbought, below 20 suggest oversold. %K crossing above %D is bullish.',
      signal: indicators.stoch_k ? (indicators.stoch_k > 80 ? 'Overbought' : indicators.stoch_k < 20 ? 'Oversold' : 'Neutral') : '—'
    },
    {
      name: 'ATR (Average True Range)',
      value: indicators.atr ? `${indicators.atr.toFixed(4)}` : 'N/A',
      formula: 'ATR = MA of True Range over 14 periods',
      explanation: 'ATR measures market volatility. Higher values indicate higher volatility. Used for setting stop-loss levels (e.g., 2× ATR from entry).',
      signal: 'Volatility measure'
    },
    {
      name: 'OBV (On-Balance Volume)',
      value: indicators.obv ? `${formatNumber(indicators.obv)}` : 'N/A',
      formula: 'OBV = OBV(prev) + Volume (if close up) or - Volume (if close down)',
      explanation: 'OBV uses volume flow to predict price changes. Rising OBV with rising price confirms uptrend. Falling OBV with rising price suggests weakness.',
      signal: 'Volume analysis'
    }
  ];

  return (
    <div className="analysis-tab">
      <div className="indicators-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px' }}>
        {technicalIndicators.map((indicator, idx) => (
          <div key={idx} className="indicator-card card" style={{ padding: '16px', background: 'var(--bg-tertiary)' }}>
            <div className="indicator-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
              <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '700' }}>{indicator.name}</h4>
              <span className="indicator-signal" style={{ fontSize: '11px', color: '#10b981', background: 'rgba(16, 185, 129, 0.1)', padding: '4px 8px', borderRadius: '4px' }}>
                {indicator.signal}
              </span>
            </div>
            <div className="indicator-value" style={{ fontSize: '16px', fontWeight: '700', marginBottom: '12px', color: 'var(--text-primary)' }}>
              {indicator.value || '—'}
            </div>
            <div className="indicator-formula" style={{ marginBottom: '12px' }}>
              <strong style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>Formula:</strong>
              <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: '6px 0 0 0', lineHeight: '1.4' }}>
                {indicator.formula}
              </p>
            </div>
            <div className="indicator-explanation">
              <strong style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>Explanation:</strong>
              <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: '6px 0 0 0', lineHeight: '1.4' }}>
                {indicator.explanation}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Backtest Tab
function BacktestTab({ backtest, symbol }) {
  if (!backtest || backtest.loading) {
    return (
      <div className="card" style={{ padding: '40px', textAlign: 'center' }}>
        <div className="spinner" style={{ margin: '0 auto 16px' }}></div>
        <p style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
          Running backtest simulation for {symbol}...
        </p>
        <p style={{ color: 'var(--text-secondary)', fontSize: '12px', marginTop: '8px' }}>
          This may take 30-60 seconds depending on data volume
        </p>
      </div>
    );
  }

  if (backtest.error) {
    return (
      <div className="card" style={{ padding: '40px', textAlign: 'center' }}>
        <p className="error" style={{ color: '#ef4444', fontSize: '16px', marginBottom: '12px' }}>
          ⚠️ Backtest Error
        </p>
        <p style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
          {backtest.error}
        </p>
        <p style={{ color: 'var(--text-secondary)', fontSize: '12px', marginTop: '16px' }}>
          Try: Switching to a different symbol, checking your internet connection, or trying again later.
        </p>
      </div>
    );
  }

  const metrics = backtest.metrics || {};
  const trades = backtest.trades || [];

  // Validate that we have actual backtest data
  if (!metrics || Object.keys(metrics).length === 0) {
    return (
      <div className="card" style={{ padding: '40px', textAlign: 'center' }}>
        <p style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
          No backtest data available for {symbol}.
        </p>
        <p style={{ color: 'var(--text-secondary)', fontSize: '12px', marginTop: '8px' }}>
          This symbol may not have sufficient intraday data for backtesting.
        </p>
      </div>
    );
  }

  return (
    <div className="backtest-tab">
      <div className="backtest-header card" style={{ marginBottom: '24px' }}>
        <h3>📊 Backtest Results for {symbol}</h3>
        <p className="backtest-description" style={{ color: 'var(--text-secondary)', marginTop: '8px' }}>
          Day trading backtest using moving average crossover strategy with risk management.
          Initial capital: $10,000, Risk per trade: 2% of capital, Period: 30 days (5-min intervals).
        </p>
      </div>

      {/* Key Metrics */}
      <div className="backtest-metrics-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px', marginBottom: '24px' }}>
        <div className="metric-card card" style={{ padding: '16px', background: 'var(--bg-tertiary)' }}>
          <span className="metric-label" style={{ display: 'block', color: 'var(--text-secondary)', fontSize: '12px', marginBottom: '8px' }}>Total Return</span>
          <span className="metric-value" style={{
            fontSize: '18px',
            fontWeight: '700',
            color: (metrics.total_return || 0) >= 0 ? '#10b981' : '#ef4444'
          }}>
            {formatPercent(metrics.total_return)}
          </span>
        </div>
        <div className="metric-card card" style={{ padding: '16px', background: 'var(--bg-tertiary)' }}>
          <span className="metric-label" style={{ display: 'block', color: 'var(--text-secondary)', fontSize: '12px', marginBottom: '8px' }}>Final Balance</span>
          <span className="metric-value" style={{ fontSize: '18px', fontWeight: '700' }}>{formatCurrency(metrics.final_balance)}</span>
        </div>
        <div className="metric-card card" style={{ padding: '16px', background: 'var(--bg-tertiary)' }}>
          <span className="metric-label" style={{ display: 'block', color: 'var(--text-secondary)', fontSize: '12px', marginBottom: '8px' }}>Total Trades</span>
          <span className="metric-value" style={{ fontSize: '18px', fontWeight: '700' }}>{metrics.total_trades}</span>
        </div>
        <div className="metric-card card" style={{ padding: '16px', background: 'var(--bg-tertiary)' }}>
          <span className="metric-label" style={{ display: 'block', color: 'var(--text-secondary)', fontSize: '12px', marginBottom: '8px' }}>Win Rate</span>
          <span className="metric-value" style={{ fontSize: '18px', fontWeight: '700', color: (metrics.win_rate || 0) > 0.5 ? '#10b981' : '#ef4444' }}>
            {formatPercent(metrics.win_rate)}
          </span>
        </div>
        <div className="metric-card card" style={{ padding: '16px', background: 'var(--bg-tertiary)' }}>
          <span className="metric-label" style={{ display: 'block', color: 'var(--text-secondary)', fontSize: '12px', marginBottom: '8px' }}>Max Drawdown</span>
          <span className="metric-value negative" style={{ fontSize: '18px', fontWeight: '700', color: '#ef4444' }}>
            {formatPercent(metrics.max_drawdown)}
          </span>
        </div>
        <div className="metric-card card" style={{ padding: '16px', background: 'var(--bg-tertiary)' }}>
          <span className="metric-label" style={{ display: 'block', color: 'var(--text-secondary)', fontSize: '12px', marginBottom: '8px' }}>Sharpe Ratio</span>
          <span className="metric-value" style={{ fontSize: '18px', fontWeight: '700' }}>{(metrics.sharpe_ratio || 0).toFixed(2)}</span>
        </div>
      </div>

      {/* Trade History */}
      {trades.length > 0 && (
        <div className="trades-section card" style={{ marginBottom: '24px' }}>
          <h3>Trade History ({trades.length} trades)</h3>
          <div className="trades-table" style={{ overflowX: 'auto', borderRadius: '8px', border: '1px solid var(--border-color)', marginTop: '12px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '600px' }}>
              <thead>
                <tr style={{ background: 'var(--bg-tertiary)', borderBottom: '1px solid var(--border-color)' }}>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '700', color: 'var(--text-secondary)' }}>Date</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '700', color: 'var(--text-secondary)' }}>Type</th>
                  <th style={{ padding: '12px', textAlign: 'right', fontWeight: '700', color: 'var(--text-secondary)' }}>Entry</th>
                  <th style={{ padding: '12px', textAlign: 'right', fontWeight: '700', color: 'var(--text-secondary)' }}>Exit</th>
                  <th style={{ padding: '12px', textAlign: 'right', fontWeight: '700', color: 'var(--text-secondary)' }}>P&L</th>
                  <th style={{ padding: '12px', textAlign: 'right', fontWeight: '700', color: 'var(--text-secondary)' }}>Return</th>
                </tr>
              </thead>
              <tbody>
                {trades.slice(0, 20).map((trade, idx) => (
                  <tr
                    key={idx}
                    style={{
                      borderBottom: '1px solid var(--border-color)',
                      background: idx % 2 === 0 ? 'transparent' : 'rgba(255, 255, 255, 0.01)'
                    }}
                  >
                    <td style={{ padding: '12px', fontSize: '13px' }}>{new Date(trade.entry_date).toLocaleDateString()}</td>
                    <td style={{
                      padding: '12px',
                      fontSize: '13px',
                      fontWeight: '700',
                      color: trade.type === 'LONG' ? '#10b981' : '#ef4444'
                    }}>
                      {trade.type}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right', fontSize: '13px' }}>{formatCurrency(trade.entry_price)}</td>
                    <td style={{ padding: '12px', textAlign: 'right', fontSize: '13px' }}>{formatCurrency(trade.exit_price)}</td>
                    <td style={{
                      padding: '12px',
                      textAlign: 'right',
                      fontSize: '13px',
                      fontWeight: '700',
                      color: (trade.pnl || 0) >= 0 ? '#10b981' : '#ef4444'
                    }}>
                      {formatCurrency(trade.pnl)}
                    </td>
                    <td style={{
                      padding: '12px',
                      textAlign: 'right',
                      fontSize: '13px',
                      fontWeight: '700',
                      color: (trade.return_pct || 0) >= 0 ? '#10b981' : '#ef4444'
                    }}>
                      {formatPercent(trade.return_pct)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Strategy Explanation */}
      <div className="backtest-explanation card">
        <h3>Strategy Explanation</h3>
        <ul style={{ fontSize: '13px', color: 'var(--text-secondary)', paddingLeft: '20px', lineHeight: '1.8' }}>
          <li><strong style={{ color: 'var(--text-primary)' }}>Entry Signal:</strong> When fast EMA (9) crosses above slow EMA (21) for LONG, or crosses below for SHORT</li>
          <li><strong style={{ color: 'var(--text-primary)' }}>Exit Signal:</strong> Opposite crossover or stop-loss/take-profit hit</li>
          <li><strong style={{ color: 'var(--text-primary)' }}>Risk Management:</strong> 2% of capital per trade, stop-loss at 1.5× ATR, take-profit at 3× ATR</li>
          <li><strong style={{ color: 'var(--text-primary)' }}>Position Sizing:</strong> Dynamic based on account balance and risk per trade</li>
          <li><strong style={{ color: 'var(--text-primary)' }}>Max Trades/Day:</strong> 5 to prevent overtrading</li>
        </ul>
      </div>
    </div>
  );
}

// AI Prediction Tab (formerly ML Model Tab)
function MLModelTab({ prediction, symbol, indicators, history, details, insights, similarStocks }) {
  // Calculate AI results (moved from OverviewTab)
  const aiResults = useMemo(() => {
    if (!indicators || !history || history.length === 0) {
      return {
        patternScore: 0,
        patterns: []
      };
    }

    // Calculate pattern score based on technical indicators
    let patternScore = 50; // Base score
    
    // RSI contribution (30-70 range is ideal)
    if (indicators.rsi) {
      const rsiScore = indicators.rsi > 30 && indicators.rsi < 70 ? 20 : 
                       indicators.rsi > 70 ? 10 : 5;
      patternScore += rsiScore;
    }
    
    // MACD contribution (positive signal is bullish)
    if (indicators.macd && indicators.macd_signal) {
      const macdBullish = indicators.macd > indicators.macd_signal;
      patternScore += macdBullish ? 15 : -10;
    }
    
    // Moving average trend (price above MA50 is bullish)
    if (indicators.sma50 && details?.currentPrice) {
      const aboveMA = details.currentPrice > indicators.sma50;
      patternScore += aboveMA ? 10 : -5;
    }

    // Volume trend analysis
    const recentVolumes = history.slice(-10).map(h => h.volume || 0);
    const avgVolume = recentVolumes.reduce((a, b) => a + b, 0) / recentVolumes.length;
    const lastVolume = recentVolumes[recentVolumes.length - 1] || 0;
    const volumeIncrease = lastVolume > avgVolume;
    
    // Price momentum (last 5 days)
    const recentPrices = history.slice(-5).map(h => h.close || 0);
    const momentum = recentPrices.length > 1 ? 
      ((recentPrices[recentPrices.length - 1] - recentPrices[0]) / recentPrices[0]) * 100 : 0;

    // Constrain score between 0-100
    patternScore = Math.max(0, Math.min(100, Math.round(patternScore)));

    // Calculate pattern percentages based on indicators
    const patterns = [
      { 
        name: 'Technical Momentum', 
        percentage: Math.round(Math.abs(momentum) * 10), 
        timeframe: '5 Days',
        increase: momentum > 0
      },
      { 
        name: 'Volume Trend', 
        percentage: volumeIncrease ? Math.round((lastVolume / avgVolume - 1) * 100) : 0,
        timeframe: '10 Days Average',
        increase: volumeIncrease
      },
      { 
        name: 'RSI Signal', 
        percentage: indicators.rsi ? Math.round(Math.abs(50 - indicators.rsi)) : 0,
        timeframe: '14 Days',
        increase: indicators.rsi && indicators.rsi < 50
      }
    ].filter(p => p.percentage > 0 && p.percentage <= 100);

    return {
      patternScore,
      patterns
    };
  }, [indicators, history, details]);

  // Calculate pattern matches (moved from OverviewTab)
  const patternMatches = useMemo(() => {
    if (!history || history.length < 60) return [];

    const sortedHistory = [...history].sort((a, b) => new Date(a.date) - new Date(b.date));
    const closes = sortedHistory.map(h => h.close || 0);
    const volumes = sortedHistory.map(h => h.volume || 0);
    const dates = sortedHistory.map(h => new Date(h.date));

    const ma50 = computeSMA(closes, 50);
    const vol20 = computeSMA(volumes, 20);
    const rsi14 = computeRSI(closes, 14);

    const matches = [];
    for (let i = 60; i < sortedHistory.length - 6; i++) {
      const aboveMa = ma50[i] && closes[i] > ma50[i];
      const volSpike = vol20[i] && volumes[i] > vol20[i] * 1.2;
      const rsiBull = rsi14[i] && rsi14[i] > 50;
      if (aboveMa && volSpike && rsiBull) {
        const forward5 = closes[i + 5] ? ((closes[i + 5] - closes[i]) / closes[i]) * 100 : null;
        matches.push({
          date: dates[i].toISOString().slice(0, 10),
          price: closes[i],
          volume: volumes[i],
          volAvg: vol20[i],
          rsi: rsi14[i],
          forward5
        });
      }
    }

    return matches.slice(-3).reverse(); // most recent three matches
  }, [history]);

  if (!prediction && !indicators && !history) {
    return <div className="card"><p>No AI prediction data available</p></div>;
  }

  const predictions = prediction?.predictions || [];
  const deepAI = prediction?.deep_ai || {};
  const confidence = prediction?.confidence || 0;

  return (
    <div className="ai-prediction-tab">
      {/* AI Result Calculation Section */}
      <div className="ai-result-section card">
        <div className="ai-result-header">
          <h3>🤖 AI Result Calculation</h3>
          <p className="ai-result-subtitle">Score 0-100 summarizing momentum, trend, and volume confluence. &gt;70 = bullish tilt; ~50 = neutral; &lt;40 = cautious.</p>
        </div>
        <div className="ai-result-content">
          <div className="pattern-score-display">
            <div className="score-circle">
              <div className="score-number">{aiResults.patternScore}</div>
              <div className="score-label">Pattern Score</div>
            </div>
            <div className="score-description">
              <p>Technical analysis score based on momentum, volume, and price indicators</p>
            </div>
          </div>
          <div className="patterns-list">
            {aiResults.patterns.map((pattern, idx) => (
              <div key={idx} className="pattern-item">
                <div className="pattern-info">
                  <span className="pattern-name">{pattern.name}</span>
                  <span className="pattern-timeframe">{pattern.timeframe}</span>
                </div>
                <div className="pattern-percentage">
                  <div className="percentage-bar">
                    <div className="percentage-fill" style={{ width: `${pattern.percentage}%` }}></div>
                  </div>
                  <span className="percentage-value">
                    {pattern.percentage}% {pattern.increase ? 'Increase' : 'Decrease'} signal
                  </span>
                </div>
              </div>
            ))}
          </div>

          {patternMatches && patternMatches.length > 0 && (
            <div className="pattern-matches">
              <div className="matches-header">Recent historical matches</div>
              <div className="matches-list">
                {patternMatches.map((m, idx) => (
                  <div key={idx} className="match-row">
                    <div>
                      <strong>{m.date}</strong> · Close {formatCurrency(m.price)}
                    </div>
                    <div className="match-meta">
                      RSI {m.rsi?.toFixed(1)} | Vol {formatNumber(m.volume)} {m.volAvg ? `(${(m.volume / m.volAvg * 100).toFixed(0)}% of avg)` : ''}
                    </div>
                    {m.forward5 != null && (
                      <div className={`match-forward ${m.forward5 >= 0 ? 'positive' : 'negative'}`}>
                        +5d move: {m.forward5 >= 0 ? '+' : ''}{m.forward5.toFixed(2)}%
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* AI Insights */}
      <div className="insights-section card" style={{ gridColumn: '1 / -1' }}>
        <h3>💡 AI Insights</h3>
        <div className="insights-list" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {insights && insights.length > 0 ? (
            insights.slice(0, 5).map((insight, idx) => (
              <div
                key={idx}
                className="insight-item"
                style={{
                  padding: '12px',
                  background: 'var(--bg-tertiary)',
                  borderRadius: '8px',
                  borderLeft: '4px solid #10b981',
                  fontSize: '13px',
                  lineHeight: '1.6',
                  color: 'var(--text-secondary)'
                }}
              >
                <p style={{ margin: 0 }}>
                  {typeof insight === 'string' ? insight : (insight.text || insight.insight || String(insight))}
                </p>
              </div>
            ))
          ) : (
            <p className="empty" style={{ color: 'var(--text-secondary)' }}>No insights available</p>
          )}
        </div>
      </div>

      {/* Similar Stocks Pattern Matching */}
      {similarStocks && similarStocks.length > 0 && (
        <div className="pattern-matching-section card" style={{ gridColumn: '1 / -1' }}>
          <div className="pattern-matching-header">
            <h3>📊 Similar Stocks Pattern Matching ({similarStocks.length})</h3>
            <p className="pattern-matching-subtitle">Stocks with similar technical patterns based on RSI, MACD, and momentum</p>
          </div>
          <div className="similar-stocks-grid">
            {similarStocks.map((stock, idx) => (
              <div key={idx} className="similar-stock-card">
                <div className="stock-header">
                  <h4>{stock.symbol}</h4>
                  {stock.name && <p className="stock-name">{stock.name}</p>}
                </div>
                <div className="stock-metrics">
                  <div className="metric">
                    <span className="label">Similarity Score</span>
                    <span className="value">{stock.score}%</span>
                  </div>
                  <div className="metric">
                    <span className="label">Change</span>
                    <span className={`value ${stock.change_percent >= 0 ? 'positive' : 'negative'}`}>
                      {stock.change_percent >= 0 ? '+' : ''}{stock.change_percent.toFixed(2)}%
                    </span>
                  </div>
                  {stock.price && (
                    <div className="metric">
                      <span className="label">Price</span>
                      <span className="value">{formatCurrency(stock.price)}</span>
                    </div>
                  )}
                  <div className="metric">
                    <span className="label">RSI</span>
                    <span className="value">{stock.rsi}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Machine Learning Price Prediction Section */}
      <div className="ml-header card" style={{ gridColumn: '1 / -1', marginTop: '24px' }}>
        <h3>🧠 Machine Learning Price Prediction</h3>
        <p>
          Using ensemble model combining Random Forest, XGBoost, and LSTM neural networks.
          Trained on historical price data, volume, and technical indicators.
        </p>
      </div>

      {/* Predictions Grid */}
      {predictions.length > 0 && (
        <div className="predictions-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', marginBottom: '24px', gridColumn: '1 / -1' }}>
          {predictions.map((pred, idx) => (
            <div key={idx} className="prediction-card card" style={{ padding: '16px', background: 'var(--bg-tertiary)' }}>
              <div className="pred-date" style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '8px' }}>
                {new Date(pred.date).toLocaleDateString()}
              </div>
              <div className="pred-price" style={{ fontSize: '18px', fontWeight: '700', marginBottom: '8px' }}>
                {formatCurrency(pred.predicted_price || 0)}
              </div>
              <div className={`pred-change ${(pred.change_percent || 0) >= 0 ? 'positive' : 'negative'}`} style={{
                fontSize: '14px',
                fontWeight: '700',
                color: (pred.change_percent || 0) >= 0 ? '#10b981' : '#ef4444',
                marginBottom: '8px'
              }}>
                {(pred.change_percent || 0) >= 0 ? '+' : ''}{(pred.change_percent || 0).toFixed(2)}%
              </div>
              <div className="pred-confidence" style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                Confidence: {((pred.confidence || 0) * 100).toFixed(1)}%
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Deep AI Analysis */}
      {deepAI && deepAI.analysis && (
        <div className="deep-ai-analysis card" style={{ marginBottom: '24px', gridColumn: '1 / -1' }}>
          <h3>Deep AI Analysis</h3>
          <p style={{ lineHeight: '1.6', color: 'var(--text-secondary)' }}>{deepAI.analysis}</p>
        </div>
      )}

      {/* Overall Confidence */}
      <div className="confidence-card card" style={{ marginBottom: '24px', padding: '16px', background: 'var(--bg-tertiary)' }}>
        <h4>Model Confidence</h4>
        <div style={{ marginTop: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              flex: 1,
              height: '8px',
              background: 'var(--bg-primary)',
              borderRadius: '4px',
              overflow: 'hidden'
            }}>
              <div style={{
                width: `${Math.min(confidence * 100, 100)}%`,
                height: '100%',
                background: confidence > 0.7 ? '#10b981' : confidence > 0.5 ? '#f59e0b' : '#ef4444',
                transition: 'width 0.3s'
              }} />
            </div>
            <span style={{ fontSize: '14px', fontWeight: '700', minWidth: '50px' }}>
              {(confidence * 100).toFixed(1)}%
            </span>
          </div>
        </div>
      </div>

      {/* Model Architecture */}
      <div className="ml-explanation card" style={{ gridColumn: '1 / -1' }}>
        <h3>Model Architecture</h3>
        <div className="model-steps" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px', marginTop: '16px' }}>
          <div className="model-step" style={{ padding: '12px', background: 'var(--bg-tertiary)', borderRadius: '8px' }}>
            <h4>1. Feature Engineering</h4>
            <ul style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: '8px 0', paddingLeft: '16px' }}>
              <li>Price indicators: SMA, EMA, RSI, MACD, Bollinger Bands</li>
              <li>Volume indicators: OBV, VWAP, Volume SMA</li>
              <li>Volatility: ATR, Historical Volatility</li>
              <li>Momentum: Stochastic, ROC, Williams %R</li>
            </ul>
          </div>
          <div className="model-step" style={{ padding: '12px', background: 'var(--bg-tertiary)', borderRadius: '8px' }}>
            <h4>2. Ensemble Training</h4>
            <ul style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: '8px 0', paddingLeft: '16px' }}>
              <li><strong>Random Forest:</strong> Non-linear patterns</li>
              <li><strong>XGBoost:</strong> Gradient boosting & feature importance</li>
              <li><strong>LSTM:</strong> Time-series patterns</li>
            </ul>
          </div>
          <div className="model-step" style={{ padding: '12px', background: 'var(--bg-tertiary)', borderRadius: '8px' }}>
            <h4>3. Prediction Aggregation</h4>
            <ul style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: '8px 0', paddingLeft: '16px' }}>
              <li>Weighted average of model predictions</li>
              <li>Confidence based on model agreement</li>
              <li>Monte Carlo simulation for uncertainty</li>
            </ul>
          </div>
          <div className="model-step" style={{ padding: '12px', background: 'var(--bg-tertiary)', borderRadius: '8px' }}>
            <h4>4. Risk Assessment</h4>
            <ul style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: '8px 0', paddingLeft: '16px' }}>
              <li>Prediction intervals (95% confidence)</li>
              <li>Volatility-adjusted forecasts</li>
              <li>Scenario analysis (bull/base/bear)</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="ml-disclaimer card" style={{ marginTop: '24px', padding: '12px', background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: '8px', gridColumn: '1 / -1' }}>
        <h4 style={{ color: '#ef4444', marginBottom: '8px' }}>⚠️ Important Disclaimer</h4>
        <p style={{ fontSize: '13px', lineHeight: '1.6', color: 'var(--text-secondary)' }}>
          Machine learning predictions are based on historical data and patterns. They do not guarantee future performance.
          Markets are influenced by many factors including news, sentiment, and macroeconomic conditions that models cannot fully capture.
          Always use predictions as one input among many in your investment decisions and never risk more than you can afford to lose.
        </p>
      </div>

      <style>{`
        .ai-prediction-tab {
          display: grid;
          gridTemplateColumns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 24px;
        }
      `}</style>
    </div>
  );
}
